import React from 'react';
import { Box, Paper, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import type { DialOpCohortRow } from '../../../types/dialingOpportunity';

const MAX_DAY_OFFSET = 14;

interface DayBucket {
  day: string;
  totalLeads: number;
  neverContacted: number;
  byOffset: Record<number, { leads: number; contacted: number }>;
}

function buildBuckets(rows: DialOpCohortRow[]): DayBucket[] {
  const byDay = new Map<string, DayBucket>();
  for (const row of rows) {
    const day = row.consumption_day ?? 'unknown';
    const bucket = byDay.get(day) ?? {
      day,
      totalLeads: 0,
      neverContacted: 0,
      byOffset: {},
    };
    if (row.day_offset === null || row.day_offset === undefined) {
      bucket.neverContacted += row.never_contacted;
      bucket.totalLeads += row.leads;
    } else {
      bucket.byOffset[row.day_offset] = {
        leads: (bucket.byOffset[row.day_offset]?.leads ?? 0) + row.leads,
        contacted: (bucket.byOffset[row.day_offset]?.contacted ?? 0) + row.contacted,
      };
      bucket.totalLeads += row.leads;
    }
    byDay.set(day, bucket);
  }
  return Array.from(byDay.values()).sort((a, b) => (a.day < b.day ? 1 : -1));
}

export default function CohortGrid({ rows }: { rows: DialOpCohortRow[] }) {
  const theme = useTheme();

  const buckets = React.useMemo(() => buildBuckets(rows), [rows]);
  const maxOffset = React.useMemo(() => {
    let max = 0;
    for (const b of buckets) {
      for (const key of Object.keys(b.byOffset)) {
        max = Math.max(max, Number(key));
      }
    }
    return Math.min(MAX_DAY_OFFSET, max);
  }, [buckets]);
  const offsets = React.useMemo(() => Array.from({ length: maxOffset + 1 }, (_, i) => i), [maxOffset]);

  if (!buckets.length) {
    return null;
  }

  const cellColor = (pct: number) => alpha(theme.palette.info.main, 0.06 + Math.min(1, pct / 100) * 0.7);
  const dayColWidth = 96;
  const cellMinWidth = 40;

  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        overflow: 'hidden',
      })}
    >
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Contact speed by consumption day
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Each row is a day leads came in. The day-offset columns show what share of that day&apos;s
          leads had their <Box component="span" sx={{ fontWeight: 600 }}>first</Box> outbound call
          connect (90+ seconds) by that day after consumption. The last two columns reconcile the
          rest of the row: leads that were dialed but whose first call{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>didn&apos;t</Box> connect, and leads not yet
          dialed at all within their 15-day window.
        </Typography>
      </Box>

      <Box sx={{ p: 2.5, overflowX: 'auto' }}>
        <Box sx={{ minWidth: dayColWidth + (offsets.length + 2) * cellMinWidth }}>
          <Stack direction="row" sx={{ mb: 0.5 }}>
            <Box sx={{ width: dayColWidth, flexShrink: 0 }} />
            {offsets.map((o) => (
              <Box key={o} sx={{ flex: 1, minWidth: cellMinWidth, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  +{o}d
                </Typography>
              </Box>
            ))}
            <Box sx={{ flex: 1.3, minWidth: cellMinWidth + 20, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Dialed, not yet connected
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: cellMinWidth, textAlign: 'center' }}>
              <Typography variant="caption" color="error" sx={{ fontWeight: 700 }}>
                Never
              </Typography>
            </Box>
          </Stack>

          {buckets.map((bucket) => {
            let cumulativeContacted = 0;
            let totalDialed = 0;
            return (
              <Stack key={bucket.day} direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                <Box sx={{ width: dayColWidth, flexShrink: 0, pr: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {dayjs(bucket.day).isValid() ? dayjs(bucket.day).format('MMM D') : bucket.day}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {bucket.totalLeads.toLocaleString()} leads
                  </Typography>
                </Box>
                {offsets.map((o) => {
                  const cell = bucket.byOffset[o];
                  cumulativeContacted += cell?.contacted ?? 0;
                  totalDialed += cell?.leads ?? 0;
                  const pct = bucket.totalLeads > 0 ? (100 * cumulativeContacted) / bucket.totalLeads : 0;
                  const tooltip = `${dayjs(bucket.day).isValid() ? dayjs(bucket.day).format('MMM D') : bucket.day} · +${o}d
Reached by this day: ${cumulativeContacted.toLocaleString()} of ${bucket.totalLeads.toLocaleString()} (${pct.toFixed(0)}%)`;
                  return (
                    <Tooltip
                      key={o}
                      title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>}
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: cellMinWidth,
                          height: 30,
                          mx: '1px',
                          borderRadius: 0.75,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: cellColor(pct),
                          cursor: 'default',
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11 }}>
                          {pct > 0 ? `${pct.toFixed(0)}%` : ''}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
                {/* Reconciliation columns: every lead is in exactly one of
                    (eventually contacted) + (dialed, first call didn't
                    connect) + (never dialed yet) -- these two plus the
                    cumulative % above always add up to 100% of the row. */}
                {(() => {
                  const dialedNoAnswer = Math.max(0, totalDialed - cumulativeContacted);
                  const dialedNoAnswerPct =
                    bucket.totalLeads > 0 ? (100 * dialedNoAnswer) / bucket.totalLeads : 0;
                  return (
                    <Tooltip
                      title={`${dialedNoAnswer.toLocaleString()} of ${bucket.totalLeads.toLocaleString()} were dialed but their first call never connected for 90+ seconds (no answer, voicemail, short call, etc.)`}
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          flex: 1.3,
                          minWidth: cellMinWidth + 20,
                          height: 30,
                          mx: '1px',
                          borderRadius: 0.75,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: dialedNoAnswer
                            ? alpha(theme.palette.warning.main, 0.14)
                            : alpha(theme.palette.text.disabled, 0.06),
                          cursor: 'default',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, fontSize: 11, color: 'warning.dark' }}
                        >
                          {dialedNoAnswer
                            ? `${dialedNoAnswer.toLocaleString()} (${dialedNoAnswerPct.toFixed(0)}%)`
                            : ''}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })()}
                <Tooltip
                  title={`${bucket.neverContacted.toLocaleString()} of ${bucket.totalLeads.toLocaleString()} never reached within their 15-day window`}
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: cellMinWidth,
                      height: 30,
                      mx: '1px',
                      borderRadius: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: bucket.neverContacted
                        ? alpha(theme.palette.error.main, 0.12)
                        : alpha(theme.palette.text.disabled, 0.06),
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11, color: 'error.main' }}>
                      {bucket.neverContacted || ''}
                    </Typography>
                  </Box>
                </Tooltip>
              </Stack>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
}
